import { ArrowRight, Pin, RefreshCw, Smartphone, Sparkles } from "lucide-react";
import { motion, useAnimate, useMotionValue, useReducedMotion, useSpring } from "motion/react";
import { type PointerEvent, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MobileEventsScreen, MobileFeedScreen, MobileMembersScreen } from "@/src/common/components/app-mock/mobile-screens";
import { CtaLink } from "@/src/common/components/cta-link";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { PhoneFrame } from "@/src/common/components/phone-frame";
import { COMMUNITY_VARIANTS, getVariantPalette, useCommunityVariant } from "@/src/common/components/product-demo/community-variant-context";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { SITE_ROUTES } from "@/src/common/constants/site-routes";

const BENEFITS: ReadonlyArray<{ idx: "1" | "2" | "3"; Icon: typeof Smartphone }> = [
	{ idx: "1", Icon: Smartphone },
	{ idx: "2", Icon: RefreshCw },
	{ idx: "3", Icon: Pin },
];

const MAX_TILT_DEG = 4;
const TILT_SPRING = { stiffness: 120, damping: 18, mass: 0.6 };

export function BrandedAppShowcase() {
	const { t } = useTranslation("home");
	const { variant, setVariant, palette } = useCommunityVariant();
	const reduced = useReducedMotion() === true;
	const [stageScope, animateStage] = useAnimate<HTMLDivElement>();
	const previousVariant = useRef(variant);
	const tiltX = useMotionValue(0);
	const tiltY = useMotionValue(0);
	const rotateX = useSpring(tiltX, TILT_SPRING);
	const rotateY = useSpring(tiltY, TILT_SPRING);

	useEffect(() => {
		if (previousVariant.current === variant) return;
		previousVariant.current = variant;
		if (reduced || !stageScope.current) return;
		void animateStage(stageScope.current, { scale: [1, 1.01, 1] }, { duration: 0.45, ease: "easeInOut" });
		void animateStage("[data-phone='left']", { rotate: [0, -4, 0] }, { duration: 0.55, ease: "easeInOut" });
		void animateStage("[data-phone='right']", { rotate: [0, 4, 0] }, { duration: 0.55, ease: "easeInOut", delay: 0.05 });
		void animateStage("[data-phone='center']", { rotate: [0, 1.5, 0] }, { duration: 0.55, ease: "easeInOut", delay: 0.1 });
	}, [variant, reduced, animateStage, stageScope]);

	const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
		if (reduced || event.pointerType !== "mouse") return;
		const rect = event.currentTarget.getBoundingClientRect();
		const px = (event.clientX - rect.left) / rect.width - 0.5;
		const py = (event.clientY - rect.top) / rect.height - 0.5;
		tiltY.set(px * MAX_TILT_DEG * 2);
		tiltX.set(-py * MAX_TILT_DEG * 2);
	};

	const resetTilt = () => {
		tiltX.set(0);
		tiltY.set(0);
	};

	return (
		<section className="bg-dark-hero relative isolate overflow-hidden py-24 md:py-32">
			<div aria-hidden="true" className="absolute inset-0 bg-grid opacity-40" />

			<div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<SectionHeading
					tone="white"
					eyebrow={
						<EyebrowBadge tone="white" icon={<Sparkles className="h-3 w-3" />}>
							{t("branded.eyebrow")}
						</EyebrowBadge>
					}
					title={t("branded.title_1")}
					titleAccent={t("branded.title_accent")}
					description={t("branded.subtitle")}
				/>

				<Reveal className="mt-12 flex flex-col items-center gap-3">
					<p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">{t("branded.theme_label")}</p>
					<div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1.5 backdrop-blur">
						{COMMUNITY_VARIANTS.map((v) => {
							const p = getVariantPalette(v);
							const isActive = v === variant;
							return (
								<button
									key={v}
									type="button"
									onClick={() => setVariant(v)}
									aria-pressed={isActive}
									className={`relative inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
										isActive ? "text-white" : "text-white/70 hover:text-white"
									}`}
								>
									{isActive ? (
										<motion.span
											layoutId="theme-pill"
											aria-hidden="true"
											className="absolute inset-0 rounded-full shadow-md"
											style={{ background: p.accent }}
											transition={reduced ? { duration: 0 } : { type: "spring", stiffness: 400, damping: 32 }}
										/>
									) : null}
									<span
										className="relative z-10 size-2 rounded-full"
										style={{ background: isActive ? "rgba(255,255,255,0.9)" : p.accent }}
									/>
									<span className="relative z-10">{p.label}</span>
								</button>
							);
						})}
					</div>
				</Reveal>

				<div className="hidden md:block md:[perspective:1400px]">
					<motion.div
						ref={stageScope}
						onPointerMove={handlePointerMove}
						onPointerLeave={resetTilt}
						style={reduced ? undefined : { rotateX, rotateY }}
						className="retint relative mx-auto mt-12 flex h-[640px] max-w-4xl items-end justify-center"
					>
						<div className="absolute left-1/2 top-12 -translate-x-[140%] scale-90 opacity-80">
							<motion.div data-phone="left">
								<PhoneFrame tilt="left" size="sm" label={t("branded.frames.events", { theme: palette.label })}>
									<MobileEventsScreen />
								</PhoneFrame>
							</motion.div>
						</div>
						<motion.div data-phone="center" className="relative z-10">
							<PhoneFrame tilt="center" size="md" label={t("branded.frames.feed", { theme: palette.label })}>
								<MobileFeedScreen />
							</PhoneFrame>
						</motion.div>
						<div className="absolute right-1/2 top-12 translate-x-[140%] scale-90 opacity-80">
							<motion.div data-phone="right">
								<PhoneFrame tilt="right" size="sm" label={t("branded.frames.members", { theme: palette.label })}>
									<MobileMembersScreen />
								</PhoneFrame>
							</motion.div>
						</div>
					</motion.div>
				</div>

				<div className="retint mx-auto mt-10 flex max-w-sm justify-center md:hidden">
					<PhoneFrame tilt="center" size="sm" label={t("branded.frames.feed", { theme: palette.label })}>
						<MobileFeedScreen />
					</PhoneFrame>
				</div>

				<div className="mt-16 grid gap-4 md:grid-cols-3">
					{BENEFITS.map(({ idx, Icon }, index) => (
						<Reveal key={idx} delay={index * 0.1}>
							<article className="h-full rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur transition-colors hover:border-white/20 hover:bg-white/10">
								<span className="inline-flex size-10 items-center justify-center rounded-xl bg-white/10 text-white">
									<Icon className="h-4 w-4" />
								</span>
								<h3 className="mt-3 text-base font-bold text-white">{t(`branded.benefit_${idx}.title`)}</h3>
								<p className="mt-1 text-sm text-white/70">{t(`branded.benefit_${idx}.body`)}</p>
							</article>
						</Reveal>
					))}
				</div>

				<Reveal delay={0.2} className="mt-12 flex justify-center">
					<CtaLink to={`${SITE_ROUTES.PRICING}#premium`} variant="white" size="lg" endContent={<ArrowRight className="h-4 w-4" />}>
						{t("branded.cta")}
					</CtaLink>
				</Reveal>
			</div>
		</section>
	);
}
